import EmptyState from '@/components/EmptyState';
import { paymentMethodLabel } from '@/features/orders/utils/payment-method';
import { ReportCard, ReportRow } from '@/features/reports/components/ReportCard';
import { usePaymentBreakdown } from '@/features/reports/hooks/useReports';
import type { DateRange } from '@/features/reports/lib/range';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import { Block, Preloader } from 'framework7-react';

/** What was taken, per payment rail - the figures you reconcile against the
 *  till and the bank. */
export function PaymentsSection({ range }: { range: DateRange }) {
  const t = useT();
  const pr = t.reports.paymentReconciliation;
  const { data, isLoading, error } = usePaymentBreakdown(range);
  const cur = data?.currency ?? 'USD';

  if (isLoading) {
    return (
      <Block className="text-align-center">
        <Preloader />
      </Block>
    );
  }
  if (error) {
    return (
      <EmptyState
        icon="exclamationmark_triangle"
        iconColor="#ff9500"
        title={pr.couldntLoad}
        description={(error as Error).message}
      />
    );
  }

  const rows = data?.byMethod.filter((m) => m.count > 0) ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        icon="creditcard"
        title={t.reports.overview.noSales}
        description={t.reports.overview.noSalesHint}
      />
    );
  }

  return (
    <div className="rpt-row">
      {rows.map((m) => (
        <ReportCard
          // Two gateways share methodKind 'ONLINE', so the brand is part of the key.
          key={`${m.methodKind}:${m.providerName ?? ''}`}
          title={`${m.providerName ?? paymentMethodLabel(m.methodKind, t.orders.methodKind)} · ${pr.paymentCount(
            m.count,
            m.count === 1 ? pr.paymentSingular : pr.paymentPlural,
          )}`}
        >
          <ReportRow
            label={pr.grossTaken}
            value={formatMoney(m.grossCents, cur)}
            style={{ borderTop: 'none' }}
          />
          <ReportRow label={pr.refunds} value={`−${formatMoney(m.refundsCents, cur)}`} />
          <ReportRow label={pr.net} value={formatMoney(m.netCents, cur)} />
          <ReportRow label={pr.tips} value={formatMoney(m.tipsCents, cur)} />
        </ReportCard>
      ))}
    </div>
  );
}
